import { useEffect, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Input,
  InputNumber,
  Modal,
  Radio,
  Skeleton,
  Space,
  Tag,
  Typography,
} from 'antd';
import { CloudDownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { usePermissions } from '../../auth/store';

export type UpdateMode = 'none' | 'optional' | 'force';

export interface UpdateConfig {
  mode: UpdateMode;
  latestBuild: number;
  minSupportedBuild: number;
  title: string;
  message: string;
  playUrl: string;
  cooldownHours: number;
}

interface AdminUpdateView extends UpdateConfig {
  updatedAt: string | null;
  updatedBy: string | null;
}

/** Same limits/allowlist the API enforces (shared-types UPDATE_LIMITS). */
export const UPDATE_TEXT_LIMITS = { title: 80, message: 300, playUrl: 200 } as const;
const PLAY_URL_RE = /^https:\/\/play\.google\.com\/store\/apps\/details\?id=com\.social\.dude(&[A-Za-z0-9_=&.-]*)?$/;

/** Client-side check mirroring the server's; the server still validates. */
export function validateUpdateConfig(c: UpdateConfig): Partial<Record<keyof UpdateConfig, string>> {
  const e: Partial<Record<keyof UpdateConfig, string>> = {};
  if (!c.title.trim()) e.title = 'Title is required';
  else if (c.title.trim().length > UPDATE_TEXT_LIMITS.title) e.title = `At most ${UPDATE_TEXT_LIMITS.title} characters`;
  if (!c.message.trim()) e.message = 'Message is required';
  else if (c.message.trim().length > UPDATE_TEXT_LIMITS.message)
    e.message = `At most ${UPDATE_TEXT_LIMITS.message} characters`;
  if (!PLAY_URL_RE.test(c.playUrl.trim())) e.playUrl = 'Must be the official Dude Google Play listing URL';
  if (!Number.isInteger(c.latestBuild) || c.latestBuild < 0) e.latestBuild = 'Enter a build number (0 = not set)';
  if (!Number.isInteger(c.minSupportedBuild) || c.minSupportedBuild < 0)
    e.minSupportedBuild = 'Enter a build number (0 = not set)';
  else if (c.latestBuild > 0 && c.minSupportedBuild > c.latestBuild)
    e.minSupportedBuild = 'Cannot be newer than the latest build';
  if (!Number.isInteger(c.cooldownHours) || c.cooldownHours < 1 || c.cooldownHours > 168)
    e.cooldownHours = '1–168 hours';
  return e;
}

const pick = (v: AdminUpdateView): UpdateConfig => ({
  mode: v.mode,
  latestBuild: v.latestBuild,
  minSupportedBuild: v.minSupportedBuild,
  title: v.title,
  message: v.message,
  playUrl: v.playUrl,
  cooldownHours: v.cooldownHours,
});

function serverError(e: unknown): { message: string; fields: Record<string, string> } {
  const data = (e as { response?: { data?: { message?: string; fields?: Record<string, string> } } })
    .response?.data;
  return { message: data?.message ?? 'Could not save app update settings', fields: data?.fields ?? {} };
}

const MODE_TAG: Record<UpdateMode, { color: string; label: string }> = {
  none: { color: 'default', label: 'No update required' },
  optional: { color: 'blue', label: 'Normal update (optional)' },
  force: { color: 'red', label: 'FORCE update (mandatory)' },
};

/**
 * Settings → App configuration → App update. Controls what the app does at
 * startup: nothing, a dismissible "Update available" prompt, or the blocking
 * force-update screen. Versions are Android BUILD numbers (versionCode, e.g.
 * 36 = v2.0.18) — the app compares builds, never version-name strings.
 * Selecting Force can lock old builds out, so it asks for confirmation.
 */
export function AppUpdateCard() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { can } = usePermissions();
  const canEdit = can('app_update.edit');
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'app-update'],
    queryFn: async () => (await api.get<AdminUpdateView>('/admin/app-config/update')).data,
  });
  const [draft, setDraft] = useState<UpdateConfig | null>(null);
  const [serverFields, setServerFields] = useState<Record<string, string>>({});

  useEffect(() => {
    if (data && draft === null) setDraft(pick(data));
  }, [data, draft]);

  const save = useMutation({
    mutationFn: async (body: UpdateConfig) =>
      (await api.patch<AdminUpdateView>('/admin/app-config/update', body)).data,
    onSuccess: (saved) => {
      message.success(
        saved.mode === 'force' ? 'Saved — FORCE update is active' : 'App update settings saved',
      );
      setServerFields({});
      qc.setQueryData(['admin', 'app-update'], saved);
      setDraft(pick(saved));
    },
    onError: (e) => {
      const err = serverError(e);
      setServerFields(err.fields);
      message.error(err.message);
    },
  });

  if (isLoading) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    );
  }
  if (isError || !data) {
    return (
      <Alert
        type="error"
        showIcon
        message="Could not load app update settings"
        action={
          <Button size="small" icon={<ReloadOutlined />} onClick={() => void refetch()}>
            Retry
          </Button>
        }
      />
    );
  }
  if (!draft) return null;

  const errors = { ...serverFields, ...validateUpdateConfig(draft) };
  const dirty = JSON.stringify(draft) !== JSON.stringify(pick(data));
  const hasErrors = Object.keys(validateUpdateConfig(draft)).length > 0;
  const set = <K extends keyof UpdateConfig>(k: K, v: UpdateConfig[K]) => {
    setDraft({ ...draft, [k]: v });
    if (serverFields[k]) setServerFields(({ [k]: _drop, ...rest }) => rest);
  };

  const onSave = () => {
    if (draft.mode === 'force' && data.mode !== 'force') {
      Modal.confirm({
        title: 'Turn FORCE update ON?',
        content:
          `Every app build below ${draft.minSupportedBuild > 0 ? `build ${draft.minSupportedBuild}` : 'the newest Play build'} ` +
          'will be blocked until the user updates. Only do this when the new version is already live on Google Play for everyone.',
        okText: 'Force update',
        okButtonProps: { danger: true },
        onOk: () => save.mutate(draft),
      });
      return;
    }
    save.mutate(draft);
  };

  const fieldError = (k: keyof UpdateConfig) =>
    errors[k] ? (
      <Typography.Text type="danger" style={{ fontSize: 12 }}>
        {errors[k]}
      </Typography.Text>
    ) : null;

  const tag = MODE_TAG[data.mode];
  return (
    <Card
      title={
        <Space>
          <CloudDownloadOutlined />
          App update
          <Tag color={tag.color} data-testid="app-update-status">
            {tag.label}
          </Tag>
        </Space>
      }
      extra={
        data.updatedAt ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Last changed {new Date(data.updatedAt).toLocaleString('en-IN')}
          </Typography.Text>
        ) : null
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 560 }}>
        <div>
          <Typography.Text>Update mode</Typography.Text>
          <div style={{ marginTop: 6 }}>
            <Radio.Group
              value={draft.mode}
              disabled={!canEdit}
              onChange={(e) => set('mode', e.target.value as UpdateMode)}
              data-testid="app-update-mode"
            >
              <Space direction="vertical">
                <Radio value="none">
                  No update required — users see nothing and use the app normally.
                </Radio>
                <Radio value="optional">
                  Normal update (optional) — builds older than “Latest build” get a dismissible
                  “Update available” prompt; “Later” continues into the app.
                </Radio>
                <Radio value="force">
                  Force update (mandatory) — builds BELOW “Minimum supported build” are blocked
                  until they update. 0 = legacy: any newer Play build is required.
                </Radio>
              </Space>
            </Radio.Group>
          </div>
          {draft.mode !== data.mode && <Tag color="gold">unsaved</Tag>}
        </div>

        <Space size="large" wrap>
          <div>
            <Typography.Text>Latest build</Typography.Text>
            <div>
              <InputNumber
                data-testid="app-update-latest"
                min={0}
                max={1_000_000}
                precision={0}
                value={draft.latestBuild}
                disabled={!canEdit}
                status={errors.latestBuild ? 'error' : undefined}
                onChange={(v) => set('latestBuild', v ?? 0)}
              />
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              versionCode on Play, e.g. 36 = v2.0.18. 0 = use what Play offers.
            </Typography.Text>
            <div>{fieldError('latestBuild')}</div>
          </div>
          <div>
            <Typography.Text>Minimum supported build</Typography.Text>
            <div>
              <InputNumber
                data-testid="app-update-min"
                min={0}
                max={1_000_000}
                precision={0}
                value={draft.minSupportedBuild}
                disabled={!canEdit}
                status={errors.minSupportedBuild ? 'error' : undefined}
                onChange={(v) => set('minSupportedBuild', v ?? 0)}
              />
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Force mode blocks builds below this. 0 = legacy Play behaviour.
            </Typography.Text>
            <div>{fieldError('minSupportedBuild')}</div>
          </div>
          <div>
            <Typography.Text>Reminder cooldown (hours)</Typography.Text>
            <div>
              <InputNumber
                data-testid="app-update-cooldown"
                min={1}
                max={168}
                precision={0}
                value={draft.cooldownHours}
                disabled={!canEdit}
                status={errors.cooldownHours ? 'error' : undefined}
                onChange={(v) => set('cooldownHours', v ?? 24)}
              />
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              How often a dismissed optional prompt may reappear. Default 24.
            </Typography.Text>
            <div>{fieldError('cooldownHours')}</div>
          </div>
        </Space>

        <div>
          <Typography.Text>Update title</Typography.Text>
          <Input
            style={{ marginTop: 4 }}
            aria-label="Update title"
            value={draft.title}
            disabled={!canEdit}
            status={errors.title ? 'error' : undefined}
            maxLength={UPDATE_TEXT_LIMITS.title + 20}
            showCount={{ formatter: ({ count }) => `${count}/${UPDATE_TEXT_LIMITS.title}` }}
            onChange={(e) => set('title', e.target.value)}
          />
          {fieldError('title')}
        </div>
        <div>
          <Typography.Text>Update message</Typography.Text>
          <Input.TextArea
            style={{ marginTop: 4 }}
            aria-label="Update message"
            value={draft.message}
            disabled={!canEdit}
            status={errors.message ? 'error' : undefined}
            maxLength={UPDATE_TEXT_LIMITS.message + 20}
            showCount={{ formatter: ({ count }) => `${count}/${UPDATE_TEXT_LIMITS.message}` }}
            autoSize={{ minRows: 2, maxRows: 5 }}
            onChange={(e) => set('message', e.target.value)}
          />
          {fieldError('message')}
        </div>
        <div>
          <Typography.Text>Google Play listing URL</Typography.Text>
          <Input
            style={{ marginTop: 4 }}
            aria-label="Google Play listing URL"
            value={draft.playUrl}
            disabled={!canEdit}
            status={errors.playUrl ? 'error' : undefined}
            maxLength={UPDATE_TEXT_LIMITS.playUrl}
            onChange={(e) => set('playUrl', e.target.value)}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Only the official Dude listing is accepted — never an arbitrary URL.
          </Typography.Text>
          {fieldError('playUrl')}
        </div>

        <Space>
          <Button
            type="primary"
            data-testid="app-update-save"
            disabled={!canEdit || !dirty || hasErrors}
            loading={save.isPending}
            onClick={onSave}
          >
            Save
          </Button>
          {dirty && (
            <Button onClick={() => setDraft(pick(data))} disabled={save.isPending}>
              Discard changes
            </Button>
          )}
        </Space>
      </Space>
    </Card>
  );
}
